/**
 * Pure helpers for MFA enrollment: phone formatting, calling-code options,
 * and request building.
 *
 * Request shapes are ported from
 * ss-ui-client/src/pages/settings/sign-in-and-security/sections/security/add-mfa-enrollment/helpers.js.
 */

import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

import { AUTH0_MFA_TYPES, CHALLENGE_TYPES, MANAGEMENT_TYPES, OOB_CHANNEL_EMAIL, TEMP_ENROLLMENT_ID_SUFFIX } from './mfa-constants';

import type { ManagementType, MfaEnrollmentMethod } from './mfa-constants';
import type { MfaEnrollment, StartMfaEnrollmentRequest } from './mfa-types';

/**
 * A country's calling code, shaped so it can be passed straight to Mantine's
 * `Select` `data` prop. `value` is the ISO 3166-1 alpha-2 country code because
 * calling codes are not unique (+1 covers US, CA, and more).
 */
export type CallingCodeOption = {
  value: string;
  label: string;
  callingCode: string;
};

/**
 * Combine a calling code and a locally-formatted number into E.164.
 * Anything that is not a digit is dropped, so users may type spaces,
 * parentheses, and dashes.
 */
export const getE164Phone = (callingCode: string, nationalNumber: string): string => {
  const digits = `${callingCode}${nationalNumber}`.replace(/\D/g, '');
  return digits === '' ? '' : `+${digits}`;
};

/**
 * Every selectable country calling code, sorted by localized country name.
 * Replaces ss-ui-client's hardcoded country-code list.
 */
export const getCallingCodeOptions = (locale = 'en'): CallingCodeOption[] => {
  const regionNames = new Intl.DisplayNames([locale], { type: 'region' });

  return getCountries()
    .map((country) => {
      const callingCode = getCountryCallingCode(country);
      return {
        value: country,
        label: `${regionNames.of(country) ?? country} (+${callingCode})`,
        callingCode,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, locale));
};

/**
 * Build the `startNewMfaEnrollmentVerification` body for a method.
 * Authenticator apps enroll via otp and need no contact detail; everything else
 * enrolls out of band on its matching channel.
 */
export const buildStartEnrollmentRequest = ({
  mfaToken,
  method,
  email,
  phone,
}: {
  mfaToken: string;
  method: MfaEnrollmentMethod;
  email?: string;
  phone?: string;
}): StartMfaEnrollmentRequest => {
  if (method === AUTH0_MFA_TYPES.totp) {
    return { mfaToken, type: CHALLENGE_TYPES.OTP };
  }

  if (method === AUTH0_MFA_TYPES.email) {
    return { mfaToken, type: CHALLENGE_TYPES.OOB, oobChannel: OOB_CHANNEL_EMAIL, email };
  }

  return { mfaToken, type: CHALLENGE_TYPES.OOB, oobChannel: method, phone };
};

const ENROLLMENT_METHOD_MANAGEMENT_TYPES: Record<MfaEnrollmentMethod, ManagementType> = {
  [AUTH0_MFA_TYPES.totp]: MANAGEMENT_TYPES.TOTP,
  [AUTH0_MFA_TYPES.email]: MANAGEMENT_TYPES.EMAIL,
  [AUTH0_MFA_TYPES.sms]: MANAGEMENT_TYPES.PHONE,
  [AUTH0_MFA_TYPES.voice]: MANAGEMENT_TYPES.PHONE,
};

/**
 * Stand-in enrollment for the confirmation step, before Auth0 has issued a real
 * authenticator id. The `{method}|temp-id` shape is what makes the existing
 * `ChallengePhase` resolve the right copy, since its predicates match on `id`.
 */
export const getPendingEnrollment = (method: MfaEnrollmentMethod, name: string): MfaEnrollment => ({
  id: `${method}|${TEMP_ENROLLMENT_ID_SUFFIX}`,
  name,
  type: ENROLLMENT_METHOD_MANAGEMENT_TYPES[method],
});

/**
 * True when the enrollment is the synthetic stand-in created by
 * `getPendingEnrollment` — i.e. Auth0 has not yet issued a real authenticator id.
 *
 * Use this to strip pending entries before merging a freshly-fetched enrollment
 * list from Auth0, so the confirmed factor is not double-tracked alongside its
 * temp copy in keyed UI state.
 */
export const getIsPendingEnrollment = (enrollment: MfaEnrollment): boolean => enrollment.id.endsWith(`|${TEMP_ENROLLMENT_ID_SUFFIX}`);

/**
 * Removing the only remaining factor would lock the user out of step-up auth,
 * so it is blocked. Mirrors ss-ui-client's enrollments table.
 */
export const getIsRemovalBlocked = (enrollments: MfaEnrollment[]): boolean => enrollments.length <= 1;
